// src/components/divesitedashboard/DashboardControls.jsx
import React from "react";
import {
  Box,
  Typography,
  Select,
  MenuItem,
  IconButton,
  useTheme,
  useMediaQuery,
  Chip,
  Stack,
  Divider,
} from "@mui/material";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import ViewListIcon from "@mui/icons-material/ViewList";
import RefreshIcon from "@mui/icons-material/Refresh";
import SettingsIcon from "@mui/icons-material/Settings";

const DashboardControls = ({
  viewMode,
  handleToggleViewMode,
  refresh,
  selectedRegion,
  setSelectedRegion,
  regions,
  limit,
  setLimit,
  availableSlots,
  onAddDivesite,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  // Mobile layout: stack controls vertically
  if (isMobile) {
    return (
      <Box sx={{ mb: 2 }}>
        {/* Main action row */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            px: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <SettingsIcon
              sx={{
                fontSize: "1.25rem",
                color: theme.palette.text.secondary,
              }}
            />
            <Typography
              variant="h6"
              sx={{
                fontSize: "1.1rem",
                fontWeight: 600,
                color: theme.palette.text.primary,
              }}
            >
              Controls
            </Typography>
          </Box>

          <IconButton
            onClick={refresh}
            aria-label="refresh divesites"
            size="large"
            sx={{
              bgcolor: theme.palette.action.hover,
              "&:hover": {
                bgcolor: theme.palette.action.selected,
                transform: "rotate(180deg)",
                transition: "transform 0.3s ease-in-out",
              },
            }}
          >
            <RefreshIcon />
          </IconButton>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Controls in mobile-friendly layout */}
        <Stack spacing={2}>
          {/* Items per page - prominent on mobile */}
          <Box>
            <Typography
              variant="body2"
              gutterBottom
              sx={{
                fontWeight: 500,
                mb: 1,
                color: theme.palette.text.primary,
              }}
            >
              Items per page
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              {[6, 12, 24, 48].map((value) => (
                <Chip
                  key={value}
                  label={value}
                  onClick={() => setLimit(value)}
                  color={limit === value ? "primary" : "default"}
                  variant={limit === value ? "filled" : "outlined"}
                  size="medium"
                  sx={{
                    minWidth: "48px",
                    height: "36px",
                    fontSize: "0.875rem",
                    fontWeight: limit === value ? 600 : 400,
                  }}
                />
              ))}
            </Box>
          </Box>

          {/* View mode toggle - if needed */}
          {handleToggleViewMode && (
            <Box>
              <Typography
                variant="body2"
                gutterBottom
                sx={{
                  fontWeight: 500,
                  mb: 1,
                  color: theme.palette.text.primary,
                }}
              >
                View Mode
              </Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Chip
                  icon={<ViewModuleIcon />}
                  label="Grid"
                  onClick={() => handleToggleViewMode("grid")}
                  color={viewMode === "grid" ? "primary" : "default"}
                  variant={viewMode === "grid" ? "filled" : "outlined"}
                  size="medium"
                  sx={{
                    height: "36px",
                    fontSize: "0.875rem",
                    fontWeight: viewMode === "grid" ? 600 : 400,
                  }}
                />
                <Chip
                  icon={<ViewListIcon />}
                  label="List"
                  onClick={() => handleToggleViewMode("table")}
                  color={viewMode === "table" ? "primary" : "default"}
                  variant={viewMode === "table" ? "filled" : "outlined"}
                  size="medium"
                  sx={{
                    height: "36px",
                    fontSize: "0.875rem",
                    fontWeight: viewMode === "table" ? 600 : 400,
                  }}
                />
              </Box>
            </Box>
          )}
        </Stack>
      </Box>
    );
  }

  // Tablet and desktop layout
  return (
    <Box sx={{ mb: 2 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: isTablet ? 1.5 : 2,
          px: isTablet ? 1 : 0,
        }}
      >
        {/* Left side controls */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: isTablet ? 1 : 1.5,
            flexWrap: "wrap",
          }}
        >
          {/* View mode toggle */}
          {handleToggleViewMode && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <IconButton
                onClick={() => handleToggleViewMode("grid")}
                color={viewMode === "grid" ? "primary" : "default"}
                aria-label="switch to grid view"
                size={isTablet ? "medium" : "small"}
                sx={{
                  bgcolor:
                    viewMode === "grid"
                      ? theme.palette.primary.main + "20"
                      : "transparent",
                  "&:hover": {
                    bgcolor:
                      viewMode === "grid"
                        ? theme.palette.primary.main + "30"
                        : theme.palette.action.hover,
                  },
                }}
              >
                <ViewModuleIcon />
              </IconButton>
              <IconButton
                onClick={() => handleToggleViewMode("table")}
                color={viewMode === "table" ? "primary" : "default"}
                aria-label="switch to table view"
                size={isTablet ? "medium" : "small"}
                sx={{
                  bgcolor:
                    viewMode === "table"
                      ? theme.palette.primary.main + "20"
                      : "transparent",
                  "&:hover": {
                    bgcolor:
                      viewMode === "table"
                        ? theme.palette.primary.main + "30"
                        : theme.palette.action.hover,
                  },
                }}
              >
                <ViewListIcon />
              </IconButton>
            </Box>
          )}

          {/* Refresh button */}
          <IconButton
            onClick={refresh}
            aria-label="refresh divesites"
            size={isTablet ? "medium" : "small"}
            sx={{
              bgcolor: theme.palette.action.hover,
              "&:hover": {
                bgcolor: theme.palette.action.selected,
                transform: "rotate(180deg)",
                transition: "transform 0.3s ease-in-out",
              },
            }}
          >
            <RefreshIcon />
          </IconButton>
        </Box>

        {/* Right side controls */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: isTablet ? 1.5 : 2,
            flexWrap: "wrap",
          }}
        >
          {/* Items per page */}
          <Box sx={{ minWidth: isTablet ? 100 : 120 }}>
            <Typography
              variant="subtitle2"
              gutterBottom
              sx={{
                fontSize: isTablet ? "0.8125rem" : "0.875rem",
                fontWeight: 500,
                mb: 0.5,
              }}
            >
              Items per page
            </Typography>
            <Select
              value={limit}
              onChange={(e) => setLimit(e.target.value)}
              size="small"
              fullWidth
              sx={{
                minHeight: "32px",
                "& .MuiSelect-select": {
                  py: 0.75,
                  fontSize: isTablet ? "0.8125rem" : "0.875rem",
                },
              }}
            >
              <MenuItem value={6}>6</MenuItem>
              <MenuItem value={12}>12</MenuItem>
              <MenuItem value={24}>24</MenuItem>
              <MenuItem value={48}>48</MenuItem>
            </Select>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default DashboardControls;
